import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ShoppingBagIcon, 
  MagnifyingGlassIcon,
  EyeIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';

const AdminOrders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const queryClient = useQueryClient();

  const { data: ordersData, isLoading, refetch } = useQuery(
    ['admin-orders', statusFilter, searchTerm],
    () => api.get('/admin/orders', {
      params: {
        status: statusFilter || undefined,
        search: searchTerm || undefined
      }
    }).then(res => res.data.data),
    {
      onError: (error) => {
        toast.error('خطأ في جلب الطلبات');
      }
    }
  );

  const updatePaymentStatusMutation = useMutation(
    ({ orderId, paymentStatus }) => 
      api.put(`/admin/orders/${orderId}/payment-status`, { payment_status: paymentStatus }),
    {
      onSuccess: () => {
        toast.success('تم تحديث حالة الدفع بنجاح');
        queryClient.invalidateQueries('admin-orders');
        if (selectedOrder) {
          setSelectedOrder(null);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'خطأ في تحديث حالة الدفع');
      }
    }
  );

  const handleApprovePayment = (orderId) => {
    if (window.confirm('هل أنت متأكد من تأكيد استلام الدفع؟')) {
      updatePaymentStatusMutation.mutate({ orderId, paymentStatus: 'paid' });
    }
  };

  const handleRejectPayment = (orderId) => {
    if (window.confirm('هل أنت متأكد من رفض الدفع؟')) {
      updatePaymentStatusMutation.mutate({ orderId, paymentStatus: 'failed' });
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'معلق', color: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'مؤكد', color: 'bg-blue-100 text-blue-800' },
      processing: { label: 'قيد التجهيز', color: 'bg-indigo-100 text-indigo-800' },
      shipped: { label: 'مشحون', color: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'مسلم', color: 'bg-green-100 text-green-800' },
      completed: { label: 'مكتمل', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-800' },
      returned: { label: 'مرتجع', color: 'bg-gray-100 text-gray-800' }
    };
    
    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'معلق', color: 'bg-yellow-100 text-yellow-800' },
      pending_verification: { label: 'بانتظار التحقق', color: 'bg-orange-100 text-orange-800' },
      paid: { label: 'مدفوع', color: 'bg-green-100 text-green-800' },
      failed: { label: 'فشل', color: 'bg-red-100 text-red-800' },
      refunded: { label: 'مسترد', color: 'bg-gray-100 text-gray-800' }
    };
    
    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ShoppingBagIcon className="w-8 h-8 text-primary-600 ml-3" />
                <h1 className="text-3xl font-bold text-gray-900">إدارة الطلبات</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                البحث
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="رقم الطلب أو اسم العميل..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                حالة الطلب
              </label>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 appearance-none"
                >
                  <option value="">جميع الحالات</option>
                  <option value="pending">معلق</option>
                  <option value="confirmed">مؤكد</option>
                  <option value="processing">قيد التجهيز</option>
                  <option value="shipped">مشحون</option>
                  <option value="delivered">مسلم</option>
                  <option value="completed">مكتمل</option>
                  <option value="cancelled">ملغي</option>
                </select>
                <FunnelIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    رقم الطلب
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    العميل
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    المبلغ الإجمالي
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    حالة الطلب
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    حالة الدفع
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    طريقة الدفع
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    التاريخ
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ordersData?.data?.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.order_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order.customer?.name || 'عميل محذوف'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.customer?.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {parseFloat(order.total_amount).toLocaleString()} جنيه
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(order.payment_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order.payment_method === 'cash_on_delivery' && 'الدفع عند الاستلام'}
                        {order.payment_method === 'bank_transfer' && 'تحويل بنكي'}
                        {order.payment_method === 'credit_card' && 'بطاقة ائتمان'}
                        {order.payment_method === 'mobile_money' && 'محفظة إلكترونية'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString('ar-SA')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-primary-600 hover:text-primary-900 inline-flex items-center"
                      >
                        <EyeIcon className="w-4 h-4 ml-1" />
                        عرض
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {ordersData?.data?.length === 0 && (
            <div className="text-center py-12">
              <ShoppingBagIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">لا توجد طلبات</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {ordersData?.last_page > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex gap-2">
              {Array.from({ length: ordersData.last_page }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 rounded ${
                    page === ordersData.current_page
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    تفاصيل الطلب #{selectedOrder.order_number}
                  </h2>
                  <p className="text-gray-600 mt-1">
                    {new Date(selectedOrder.created_at).toLocaleString('ar-SA')}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Customer Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">معلومات العميل</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">الاسم:</span>
                    <span className="mr-2 font-medium">{selectedOrder.customer?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">البريد:</span>
                    <span className="mr-2 font-medium">{selectedOrder.customer?.email}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">العنوان:</span>
                    <span className="mr-2 font-medium">
                      {typeof selectedOrder.shipping_address === 'string' 
                        ? selectedOrder.shipping_address 
                        : selectedOrder.shipping_address?.address}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">المنتجات</h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-3 border rounded-lg">
                      {item.product?.images?.[0]?.url && (
                        <img
                          src={item.product.images[0].url}
                          alt={item.product.name}
                          className="w-16 h-16 rounded object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">{item.product?.name}</p>
                        <p className="text-sm text-gray-600">
                          الكمية: {item.quantity} × {parseFloat(item.price).toLocaleString()} جنيه
                        </p>
                      </div>
                      <div className="text-left font-medium">
                        {parseFloat(item.total).toLocaleString()} جنيه
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="border-t pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">المجموع الفرعي:</span>
                    <span className="font-medium">{parseFloat(selectedOrder.subtotal).toLocaleString()} جنيه</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">الشحن:</span>
                    <span className="font-medium">{parseFloat(selectedOrder.shipping_cost).toLocaleString()} جنيه</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">الضريبة:</span>
                    <span className="font-medium">{parseFloat(selectedOrder.tax_amount).toLocaleString()} جنيه</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>الإجمالي:</span>
                    <span className="text-primary-600">{parseFloat(selectedOrder.total_amount).toLocaleString()} جنيه</span>
                  </div>
                </div>
              </div>

              {/* Transfer Receipt */}
              {selectedOrder.payment_method === 'bank_transfer' && selectedOrder.transfer_receipt && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">إشعار التحويل البنكي</h3>
                  <div className="space-y-2">
                    <a
                      href={`${process.env.REACT_APP_STORAGE_URL || 'http://localhost:8000/storage'}/${selectedOrder.transfer_receipt}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary-600 hover:text-primary-800 underline font-medium"
                    >
                      📎 عرض الإشعار في نافذة جديدة
                    </a>
                    <div className="mt-3">
                      <img
                        src={`${process.env.REACT_APP_STORAGE_URL || 'http://localhost:8000/storage'}/${selectedOrder.transfer_receipt}`}
                        alt="إشعار التحويل"
                        className="max-w-md rounded-lg border shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <p className="text-sm text-gray-600 hidden">
                        لا يمكن عرض الصورة. 
                        <a 
                          href={`${process.env.REACT_APP_STORAGE_URL || 'http://localhost:8000/storage'}/${selectedOrder.transfer_receipt}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-primary-600 underline mr-1"
                        >
                          تحميل الملف
                        </a>
                      </p>
                    </div>

                    {/* Payment Status Actions */}
                    {selectedOrder.payment_status === 'pending_verification' && (
                      <div className="mt-4 flex gap-3">
                        <button
                          onClick={() => handleApprovePayment(selectedOrder.id)}
                          disabled={updatePaymentStatusMutation.isLoading}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          <CheckCircleIcon className="w-5 h-5 ml-2" />
                          تأكيد استلام الدفع
                        </button>
                        <button
                          onClick={() => handleRejectPayment(selectedOrder.id)}
                          disabled={updatePaymentStatusMutation.isLoading}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                          <XCircleIcon className="w-5 h-5 ml-2" />
                          رفض الدفع
                        </button>
                      </div>
                    )}

                    {selectedOrder.payment_status === 'paid' && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800 font-medium">
                          ✓ تم تأكيد استلام الدفع
                        </p>
                      </div>
                    )}

                    {selectedOrder.payment_status === 'failed' && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-800 font-medium">
                          ✗ تم رفض الدفع
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">ملاحظات</h3>
                  <p className="text-gray-700">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminOrders;
